import React, { MouseEvent, FC, useCallback, useState, useEffect, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import styled from 'styled-components';
import _map from 'lodash/map';
import _filter from 'lodash/filter';
import _findIndex from 'lodash/findIndex';
import _uniqBy from 'lodash/uniqBy';
import { gqlCountryEnum as COUNTRY } from '@minecraft.graphql-types';
import { AppType } from '@blocs.common';
import { useTranslation } from '@blocs.i18n';
import Loader from '@minecraft.loader';
import { media } from '@minecraft.themes';
import { WindowWidthContext } from '@minecraft.utils';
import { useModal } from '@minecraft.modal';
import { ID, SelectOption } from '../../shared/types';
import { QUERY_PRESENTATION_PUBLIC_FOLDER } from '../shared/graphqls';
import getRouterWithPrefix from '../../shared/utils/getRouterWithPrefix';
import SeeAllTalentModal from '../common/SeeAllTalentModal';
import { getPresentationUserInfo, setPresentationUserInfo } from '../../shared/utils/picksUtils';
import UserNameModal from '../common/UserNameModal';
import RecipientDetailViewSideBar from './RecipientDetailViewSideBar';
import RecipientDetailViewMainContent from './RecipientDetailViewMainContent';

const FETCH_MORE_NETWORK_STATUS_CODE = 3;
const TALENTS_PAGE_SIZE_BY_DEFAULT = 10;

const Container = styled.div`
  display: flex;
  flex-direction: column;

  ${media.sm`
    flex-direction: row;
    overflow: hidden;
    height: 100vh;
  `}
`;

const StyledSideBar = styled(RecipientDetailViewSideBar)`
  ${media.sm`
    flex: 0 0 auto;
    overflow: hidden;
    overflow-y: auto;
    max-width: 11.0625rem;
    width: 100%;
  `}
`;

const StyledMainContent = styled(RecipientDetailViewMainContent)`
  ${media.sm`
    flex: auto;
    overflow: auto;
  `}
`;

type Props = {
  isShareSelectsPresentation?: boolean;
  appType?: AppType;
  cdnRoot: string;
  companyName: string;
  presentationTypeName: string;
  iconName: string;
  routerPrefix?: string;
  accountCountryCode?: COUNTRY;
};

const RecipientDetailView: FC<Props> = ({
  isShareSelectsPresentation = false,
  appType,
  cdnRoot,
  companyName,
  presentationTypeName,
  iconName,
  routerPrefix,
  accountCountryCode,
}) => {
  const { t } = useTranslation();

  const { lessSm } = WindowWidthContext.useWidthData();
  const routerHistory = useHistory();
  const { guid, folderId, talentGuid } = useParams();
  const [isSeeAllTalentModalOpen, setSeeAllTalentModalOpen] = useState<boolean>(false);
  const [hasToBeFetchedMore, setHasToBeFetchedMore] = useState<boolean>(false);
  const [selectedFolderId, setSelectedFolderId] = useState<ID>(folderId);
  const [folderOptions, setFolderOptions] = useState<SelectOption[]>([]);
  const { isOpen: isUserNameModalOpen, toggleModal: userNameModalToggle } = useModal();
  const userInfo = useMemo(() => getPresentationUserInfo(), [isUserNameModalOpen]);
  const { data, loading, fetchMore, networkStatus } = useQuery(QUERY_PRESENTATION_PUBLIC_FOLDER, {
    variables: {
      guid,
      folderId: [Number(selectedFolderId)],
      pageInput: {
        pageSize: TALENTS_PAGE_SIZE_BY_DEFAULT,
        pageNumber: 1,
      },
      withShareSelectsData: isShareSelectsPresentation,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  const handleSeeAllTalentModalToggle = useCallback(() => {
    setSeeAllTalentModalOpen(!isSeeAllTalentModalOpen);
  }, [isSeeAllTalentModalOpen]);

  const handleFolderChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newSelectedFolderId = event.currentTarget.value;

    setSelectedFolderId(newSelectedFolderId);
  }, []);

  const handleTalentsFetchMore = useCallback(
    async (pageSize: number = TALENTS_PAGE_SIZE_BY_DEFAULT): Promise<void> => {
      const hasNextPage =
        data?.getPresentationPublic?.folder[0]?.talents?.length < data?.getPresentationPublic?.folder[0]?.talentCount;
      const nextPageNumber = Math.floor(data?.getPresentationPublic?.folder[0]?.talents?.length / pageSize) + 1;

      if (!hasNextPage) return;

      try {
        await fetchMore({
          variables: {
            pageInput: {
              pageSize,
              pageNumber: nextPageNumber,
            },
          },
          // concatenate old and new entries
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prev;

            setHasToBeFetchedMore(false);

            return {
              getPresentationPublic: {
                ...prev.getPresentationPublic,
                folder: [
                  {
                    ...prev.getPresentationPublic.folder[0],
                    talents: _uniqBy(
                      [
                        ...prev.getPresentationPublic.folder[0].talents,
                        ...fetchMoreResult.getPresentationPublic.folder[0].talents,
                      ],
                      'id'
                    ),
                  },
                ],
              },
            };
          },
        });
      } catch (errorGraphql) {
        console.error(errorGraphql);
      }
    },
    [data]
  );

  const handleTalentChange = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const newTalentGuid = event.currentTarget.value;
      const newRouter = getRouterWithPrefix(
        routerPrefix,
        `${guid}/folder/${selectedFolderId}/talents/${newTalentGuid}`
      );

      routerHistory.push(newRouter);

      if (isSeeAllTalentModalOpen) {
        handleSeeAllTalentModalToggle();
      }
    },
    [isSeeAllTalentModalOpen, guid, selectedFolderId, routerHistory, handleSeeAllTalentModalToggle]
  );

  const handleScroll = (event: MouseEvent<HTMLDivElement>): void => {
    if (isShareSelectsPresentation) return;

    const { currentTarget } = event;
    // fetch more talents if the scroll reaches the end of the list

    if (currentTarget.scrollTop + currentTarget.clientHeight >= currentTarget.scrollHeight) {
      handleTalentsFetchMore().catch((e) => console.error(e));
    }
  };

  const userNameSubmit = useCallback(
    (value: string): void => {
      setPresentationUserInfo({
        name: value,
      });

      userNameModalToggle();
    },
    [userNameModalToggle]
  );

  useEffect(() => {
    if (data?.getPresentationPublic) {
      const presentationPublicFolders = data.getPresentationPublic.folders || [];
      const filteredPresentationPublicFolders = _filter(
        presentationPublicFolders,
        (folder) => !!folder.talentCount || !!folder.submissionCount
      );
      const currentTalentIndex = _findIndex(
        data?.getPresentationPublic?.folder[0]?.talents,
        (talent) => talent['guid'] === talentGuid
      );
      const latestTalentIndex = data?.getPresentationPublic?.folder[0]?.talents?.length - 1;

      setFolderOptions(
        _map(filteredPresentationPublicFolders, (folder) => ({
          value: String(folder.id),
          label: folder.name,
        }))
      );

      // fetch more talents if the current talent is not on the talent list or if the current talent is near the end of the list
      setHasToBeFetchedMore(
        !isShareSelectsPresentation && (currentTalentIndex === -1 || currentTalentIndex >= latestTalentIndex - 1)
      );
    }
  }, [data, talentGuid]);

  // If selectedFolderId has been changed via the folder dropdown then display the first talent of the selected folder
  useEffect(() => {
    const presentationPublicTalents =
      data?.getPresentationPublic?.folder[0]?.talents ||
      data?.getPresentationPublic?.folder[0]?.groups[0]?.submissions ||
      [];
    const firstTalentGuid = presentationPublicTalents[0]?.guid || presentationPublicTalents[0]?.profile?.guid;

    if ((!talentGuid || selectedFolderId !== folderId) && firstTalentGuid) {
      const newRouter = getRouterWithPrefix(
        routerPrefix,
        `${guid}/folder/${selectedFolderId}/talents/${firstTalentGuid}`
      );

      routerHistory.push(newRouter);
    }
  }, [routerPrefix, talentGuid, selectedFolderId, data?.getPresentationPublic]);

  useEffect(() => {
    // fetch more talents automatically on mobile view
    if (!isShareSelectsPresentation && lessSm && !isSeeAllTalentModalOpen && hasToBeFetchedMore) {
      handleTalentsFetchMore(100).catch((e) => console.error(e));
    }
  }, [lessSm, isSeeAllTalentModalOpen, hasToBeFetchedMore]);

  // don't display common Loader if fetchMore is triggered
  if (loading && networkStatus !== FETCH_MORE_NETWORK_STATUS_CODE) return <Loader />;

  const presentationPublicData = data?.getPresentationPublic;
  const currentTalentIndex = _findIndex(
    presentationPublicData?.folder[0]?.talents || presentationPublicData?.folder[0]?.groups[0]?.submissions,
    (talent) => talent['guid'] === talentGuid || talent?.profile?.guid === talentGuid
  );
  const talentCount =
    presentationPublicData?.folder[0]?.talentCount ||
    presentationPublicData?.folder[0]?.talents?.length ||
    presentationPublicData?.folder[0]?.submissionCount ||
    presentationPublicData?.folder[0]?.groups[0]?.submissions?.length;
  const talentList =
    presentationPublicData?.folder[0]?.talents || presentationPublicData?.folder[0]?.groups[0]?.submissions || [];

  return (
    <Container>
      {!presentationPublicData.linkExpired && presentationPublicData?.folder[0] && (
        <StyledSideBar
          isNextPageLoading={loading || networkStatus === FETCH_MORE_NETWORK_STATUS_CODE}
          hasNextPage={talentList?.length < talentCount}
          currentTalentGuid={talentGuid}
          backToHomeLinkPath={getRouterWithPrefix(routerPrefix, `${guid}`)}
          gridViewLinkPath={getRouterWithPrefix(routerPrefix, `${guid}/folder/${selectedFolderId}/talents`)}
          folderName={presentationPublicData?.folder[0]?.name}
          talentCount={talentCount}
          selectedFolder={String(selectedFolderId)}
          folderOptions={folderOptions}
          currentTalentPosition={currentTalentIndex + 1}
          prevTalentGuid={talentList[currentTalentIndex - 1]?.guid}
          nextTalentGuid={talentList[currentTalentIndex + 1]?.guid}
          talentList={talentList}
          handleFolderChange={handleFolderChange}
          handleSeeAllTalentModalOpen={handleSeeAllTalentModalToggle}
          handleTalentChange={handleTalentChange}
          handleScroll={handleScroll}
          appType={appType}
          userName={userInfo.name}
          userNameModalToggle={userNameModalToggle}
          presentationId={data?.getPresentationPublic?.id}
          presentationGuid={guid}
          pickedBy={talentList[currentTalentIndex]?.pickedBy}
          pickCount={talentList[currentTalentIndex]?.pickCount}
          isShareSelectsPresentation={isShareSelectsPresentation}
        />
      )}
      <StyledMainContent
        isShareSelectsPresentation={isShareSelectsPresentation}
        roleId={talentList[currentTalentIndex]?.roleId}
        darkTheme={false}
        currentTalentGuid={talentGuid}
        cdnRoot={cdnRoot}
        companyName={companyName}
        presentationTypeName={presentationTypeName}
        presentationPublicData={presentationPublicData}
        iconName={iconName}
        expired={presentationPublicData?.linkExpired || !presentationPublicData?.folder[0]}
        accountCountryCode={accountCountryCode}
        userName={userInfo.name}
        userNameModalToggle={userNameModalToggle}
        presentationId={data?.getPresentationPublic?.id}
        presentationGuid={guid}
        pickedBy={talentList[currentTalentIndex]?.pickedBy}
        pickCount={talentList[currentTalentIndex]?.pickCount}
        folderId={Number(selectedFolderId)}
        showSummaryWithFolderCustomization={!!selectedFolderId}
      />
      {isSeeAllTalentModalOpen && (
        <SeeAllTalentModal
          isNextPageLoading={loading || networkStatus === FETCH_MORE_NETWORK_STATUS_CODE}
          hasNextPage={talentList?.length < talentCount}
          currentTalentGuid={talentGuid}
          talentCount={talentCount}
          talentList={talentList}
          handleToggleClick={handleSeeAllTalentModalToggle}
          handleTalentChange={handleTalentChange}
          handleScroll={handleScroll}
        />
      )}
      {isUserNameModalOpen && (
        <UserNameModal
          currentValue={userInfo.name}
          hintText={t('common:recipientDetailView.userNameModal.hintText')}
          submitLabel={t('common:button.save')}
          handleModalSubmit={userNameSubmit}
          handleModalToggle={userNameModalToggle}
        />
      )}
    </Container>
  );
};

export default RecipientDetailView;
